import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { httpRequest } from "@follaw-fr/utils/request.utils";
import {
  getEndDateFromPreset,
  getStartDateFromPreset,
} from "@follaw-fr/utils/date.utils";
import { handleNetworkError } from "@follaw-fr/utils/handleNetworkError";
import cloneDeep from "lodash/cloneDeep";
import { ajouterSuivi } from "@follaw-fr/utils/follaw-depuis-mail";

export const TIME_INTERVALS = {
  LAST_24H: "last_24h",
  LAST_WEEK: "last_week",
  LAST_MONTH: "last_month",
  LAST_YEAR: "last_year",
  LEGI_15: "legislature_15",
  LEGI_16: "legislature_16",
  LEGI_17: "legislature_17",
  FREE: "free",
};

export const FOLLAWED_FEATURES = {
  AMENDMENTS: "follawed_amendments",
  JORFS: "follawed_jorfs",
  LEGIDOCS: "follawed_legi_docs",
  QUESTIONS: "follawed_questions",
  PETITIONS: "follawed_petitions",
  TWEET_AUTHORS: "follawed_tweet_authors",
  PRM: "follawed_prms",
  COMMISSION: "follawed_commissions",
  LOBBIES: "follawed_lobbies",
  ORDRE_DU_JOUR_SN: "follawed_ordre_du_jour",
  FEUILLE_VERTE: "follawed_feuille_verte",
  COMPTES_RENDUS: "follawed_compte_rendus",
  AGENDA: "follawed_events",
  NEWS: "follawed_news",
  NOMINATIONS: "follawed_nominations",
  ARTICLES: "follawed_articles",
  PUBLICATIONS: "follawed_publications",
};

const INITIAL_SELECTED_INTERVAL = {
  selected_from_date: null,
  selected_to_date: null,
  selected_time_range: TIME_INTERVALS.LEGI_17,
};

const INITIAL_FOLLAWED_VALUES = {
  [FOLLAWED_FEATURES.AMENDMENTS]: [],
  [FOLLAWED_FEATURES.JORFS]: [],
  [FOLLAWED_FEATURES.LEGIDOCS]: [],
  [FOLLAWED_FEATURES.QUESTIONS]: [],
  [FOLLAWED_FEATURES.PETITIONS]: [],
  [FOLLAWED_FEATURES.TWEET_AUTHORS]: [],
  [FOLLAWED_FEATURES.PRM]: [],
  [FOLLAWED_FEATURES.COMMISSION]: [],
  [FOLLAWED_FEATURES.LOBBIES]: [],
  [FOLLAWED_FEATURES.ORDRE_DU_JOUR_SN]: [],
  [FOLLAWED_FEATURES.FEUILLE_VERTE]: [],
  [FOLLAWED_FEATURES.COMPTES_RENDUS]: [],
  [FOLLAWED_FEATURES.AGENDA]: [],
  [FOLLAWED_FEATURES.NEWS]: [],
  [FOLLAWED_FEATURES.NOMINATIONS]: [],
  [FOLLAWED_FEATURES.ARTICLES]: [],
  [FOLLAWED_FEATURES.PUBLICATIONS]: [],
};

const INITIAL_SHARED_FILTERS = {
  selected_keyword: "",
  selected_thematics: [],
  selected_subthematics: [],
};

export const useUserStore = defineStore("user", () => {
  const toast = useToast();
  const route = useRoute();
  const router = useRouter();

  const isTopbarReady = ref(false);
  const dashboards = ref([]);
  const thematics = ref([]);
  const subthematics = ref([]);
  const productTourCompleted = ref(false);
  const onboardingCompleted = ref(false);
  const loading = ref(false);
  const selectedDashboardId = ref(null);
  const selectedDashboardFilters = ref(null);
  const selectedInterval = ref({ ...INITIAL_SELECTED_INTERVAL });
  const follawed = ref(cloneDeep(INITIAL_FOLLAWED_VALUES));

  const isSelectIntervalDisabled = computed(() => {
    return (
      route.name === "Agenda" ||
      route.path.includes("/alerts") ||
      route.path.includes("/legislatives")
    );
  });

  // Those filters are shared between services, and are local to the front-end
  // (not saved in the back-end as preference)
  const sharedFilters = ref(cloneDeep(INITIAL_SHARED_FILTERS));
  const searchBoxCurrentValue = ref(sharedFilters.value.selected_keyword);

  const reset = () => {
    isTopbarReady.value = false;
    dashboards.value = [];
    thematics.value = [];
    subthematics.value = [];
    productTourCompleted.value = false;
    onboardingCompleted.value = false;
    loading.value = false;
    selectedDashboardId.value = null;
    selectedDashboardFilters.value = null;
    selectedInterval.value = { ...INITIAL_SELECTED_INTERVAL };
    follawed.value = cloneDeep(INITIAL_FOLLAWED_VALUES);
    sharedFilters.value = cloneDeep(INITIAL_SHARED_FILTERS);
    searchBoxCurrentValue.value = sharedFilters.value.selected_keyword;
  };

  // `destination` : pendant une garde du routeur, `route` est encore la page d'où l'on vient
  // (vide à l'ouverture d'un onglet) ; la veille à lire est celle de la page où l'on va.
  const getUserAndFilters = async (destination = route) => {
    const dashboardDemande = destination.query.dashboard;
    try {
      const response = await httpRequest("get", "/users/topbar");
      if (response.data) {
        dashboards.value = response.data.dashboards;
        selectedDashboardFilters.value =
          response.data.selected_dashboard_content?.filters;
        thematics.value = response.data.thematics;
        subthematics.value = response.data.subthematics;
        productTourCompleted.value = response.data.product_tour_completed;
        onboardingCompleted.value = response.data.onboarding_completed;

        if (
          dashboardDemande &&
          dashboards.value.find(
            (dashboard) => dashboard._id === parseInt(dashboardDemande)
          )
        ) {
          selectedDashboardId.value = parseInt(dashboardDemande);
        } else {
          selectedDashboardId.value = response.data.selected_dashboard;

          // Sur /forbidden, le routeur peut avoir recopié une veille de l'adresse précédente : y renvoyer bouclerait.
          if (
            dashboardDemande &&
            destination.path !== "/forbidden" &&
            !dashboards.value.find(
              (dashboard) => dashboard._id === parseInt(dashboardDemande)
            )
          ) {
            return router.push({
              path: "/forbidden",
              query: {
                accessTo: destination.path,
                withDashboard: dashboardDemande,
              },
            });
          }
        }
        if (typeof selectedDashboardId.value !== "number") {
          toast.warning(
            "Vous n'avez pas sélectionné de veille.\nChoisissez une veille pour utiliser la plateforme."
          );
        }

        Object.keys(selectedInterval.value).forEach((key) => {
          selectedInterval.value[key] = response.data[key];
        });
        Object.keys(follawed.value).forEach((key) => {
          follawed.value[key] = response.data[key];
        });
      }

      isTopbarReady.value = true;
    } catch (err) {
      handleNetworkError(err);
    }
  };

  const updateFilters = async (payload) => {
    loading.value = true;

    if (
      payload.selected_time_range &&
      payload.selected_time_range !== TIME_INTERVALS.FREE
    ) {
      delete payload.selected_from_date;
      delete payload.selected_to_date;
    }

    try {
      await httpRequest("PATCH", `/users`, payload);
    } catch (err) {
      handleNetworkError(err);
    }

    loading.value = false;
  };

  const isItemFollawed = (feature, id) => {
    return follawed.value[feature]?.includes(id);
  };

  const toggleFollawed = async (feature, id) => {
    const isFollawed = isItemFollawed(feature, id);
    if (isFollawed) {
      follawed.value[feature] = follawed.value[feature].filter(
        (elem) => elem !== id
      );
    } else {
      follawed.value[feature] = [...(follawed.value[feature] || []), id];
    }

    const payload = {
      [feature]: follawed.value[feature],
    };

    try {
      await httpRequest("PATCH", `/users`, payload);
    } catch (err) {
      handleNetworkError(err);
    }
  };

  // Ajoute (sans jamais retirer) un élément à la liste suivie, pour le lien « Follaw » d'un mail d'alerte.
  // L'état local n'est modifié QU'APRÈS la réponse du serveur, et rien n'est envoyé si la liste n'est pas connue.
  // Renvoie "ajoute" | "deja_suivi" | "liste_absente" | "echec".
  const addFollawed = async (feature, id) => {
    const ajout = ajouterSuivi(follawed.value[feature], id);
    if (!ajout) return "liste_absente";
    if (ajout.dejaSuivi) return "deja_suivi";

    try {
      await httpRequest("PATCH", `/users`, { [feature]: ajout.liste });
    } catch (err) {
      try {
        handleNetworkError(err);
      } catch {
        // handleNetworkError relance après avoir affiché l'erreur : l'échec est rendu par la valeur de retour.
      }
      return "echec";
    }
    follawed.value[feature] = ajout.liste;
    return "ajoute";
  };

  const sharedFiltersAsPayload = computed(() => {
    const payload = {};

    payload.dashboard_id = selectedDashboardId.value;

    if (isSelectIntervalDisabled.value) {
      delete payload.start;
      delete payload.end;
    } else if (
      selectedInterval.value.selected_time_range === TIME_INTERVALS.FREE
    ) {
      payload.start = selectedInterval.value.selected_from_date;
      payload.end = selectedInterval.value.selected_to_date;
    } else {
      payload.start =
        getStartDateFromPreset(selectedInterval.value.selected_time_range) +
        "T00:00:00+02:00";
      // End parameter is optional and only for some presets
      const end = getEndDateFromPreset(
        selectedInterval.value.selected_time_range
      );
      if (end) {
        payload.end = end + "T23:59:00+02:00";
      }
    }

    if (sharedFilters.value.selected_keyword?.length > 0) {
      payload.filter = sharedFilters.value.selected_keyword;
    }

    if (sharedFilters.value.selected_thematics?.length > 0) {
      payload.thematic_ids = sharedFilters.value.selected_thematics;
    }

    if (sharedFilters.value.selected_subthematics?.length > 0) {
      payload.subthematic_ids = sharedFilters.value.selected_subthematics;
    }

    return payload;
  });

  const selectedDashboardName = computed(() => {
    return dashboards.value.find((d) => d._id === selectedDashboardId.value)
      ?.name;
  });

  const confirmSearchBox = () => {
    sharedFilters.value.selected_keyword = searchBoxCurrentValue.value;
  };

  const setSelectedDashboard = (id) => {
    if (selectedDashboardId.value !== id) {
      selectedDashboardId.value = id;
      updateFilters({
        selected_dashboard: id,
      });
    }
  };

  const getThematicById = (id) => {
    return thematics.value?.find((thematic) => thematic._id === id);
  };

  const getSubthematicById = (id) => {
    for (const thematic of thematics.value) {
      const subthematic = thematic.subthematics.find((s) => s._id === id);
      if (subthematic) return subthematic;
    }
    return null;
  };

  watch(
    [
      () => route.query.search,
      () => route.query.thematics,
      () => route.query.subthematics,
    ],
    ([newSearch, newThematics, newSubthematics]) => {
      sharedFilters.value.selected_keyword = newSearch ?? "";
      searchBoxCurrentValue.value = newSearch ?? "";
      sharedFilters.value.selected_thematics = newThematics
        ? newThematics.split(",").map((id) => parseInt(id))
        : [];
      sharedFilters.value.selected_subthematics = newSubthematics
        ? newSubthematics.split(",").map((id) => parseInt(id))
        : [];
    }
  );

  return {
    isTopbarReady,
    dashboards,
    thematics,
    subthematics,
    selectedDashboardId,
    selectedDashboardName,
    selectedDashboardFilters,
    selectedInterval,
    getThematicById,
    getSubthematicById,
    follawed,
    sharedFilters,
    loading,
    onboardingCompleted,
    productTourCompleted,
    searchBoxCurrentValue,
    sharedFiltersAsPayload,
    reset,
    setSelectedDashboard,
    isItemFollawed,
    getUserAndFilters,
    updateFilters,
    confirmSearchBox,
    toggleFollawed,
    addFollawed,
  };
});
